import {isLabelArchived} from '@github-ui/label-token/RepositoryLabelToken'
import {testIdProps} from '@github-ui/test-id-props'
import {ArchiveIcon} from '@primer/octicons-react'
import type {SelectPanelItemProps as ItemProps} from '@primer/react'
import {useCallback} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {SuggestedLabel} from '../../api/memex-items/contracts'
import {SanitizedHtml} from '../../components/dom/sanitized-html'
import type {MemexItemModel} from '../../models/memex-item-model'
import {useFetchSuggestedLabels} from '../../state-providers/suggestions/use-fetch-suggested-labels'
import {useUpdateItem} from '../use-update-item'
import styles from './use-labels-editor.module.css'

export const getNameHtml = (option: SuggestedLabel) => {
  return option.nameHtml
}

const LabelItemContent = ({label}: {label: SuggestedLabel}) => {
  const labelName = <SanitizedHtml className={styles.SanitizedHtml}>{label.nameHtml}</SanitizedHtml>

  return (
    <>
      {isLabelArchived(label) ? (
        <span className={styles.ArchivedLabelContent}>
          <ArchiveIcon size={12} className={styles.ArchivedLabelIcon} aria-label="Archived" />
          {labelName}
        </span>
      ) : (
        labelName
      )}
    </>
  )
}

export const convertOptionToItem = (option: SuggestedLabel): ItemProps & SuggestedLabel => {
  const color = `#${option.color}`
  return {
    ...option,
    leadingVisual() {
      return <div style={{'--option-color': color}} className={styles.Box} />
    },
    children: <LabelItemContent label={option} />,
    ...testIdProps('table-cell-editor-row'),
  }
}

type UseLabelsEditorProps = {
  model: MemexItemModel
  onSaved?: () => void
}

export function useLabelsEditor({model, onSaved}: UseLabelsEditorProps) {
  const {updateItem} = useUpdateItem()
  const {fetchSuggestedLabels} = useFetchSuggestedLabels()

  const fetchOptions = useCallback(() => {
    fetchSuggestedLabels(model)
  }, [model, fetchSuggestedLabels])

  const saveSelected = useCallback(
    async (nextSelected: Array<SuggestedLabel>) => {
      await updateItem(model, {
        dataType: MemexColumnDataType.Labels,
        value: nextSelected,
      })
      if (onSaved) {
        onSaved()
      }
    },
    [model, onSaved, updateItem],
  )

  return {
    fetchOptions,
    saveSelected,
  }
}
